/**
 * HighlightsRow — curated 4-card carousel of the creator's best/recent clips.
 *
 * Pulls from the same content pool as the CLIPS tab but trimmed to the
 * top-4 by recency (placeholder selection logic). When the creator has no
 * clips yet, renders 4 placeholder cards that mirror the design so the
 * page layout reads as intentional.
 */

import React, { useMemo } from "react";
import { FaPlay, FaEye, FaClock } from "react-icons/fa";


import { useGetDropTwitchClips } from "@/hooks/drop/useGetDropTwitchClips";
import { useGetDropTwitchVods } from "@/hooks/drop/useGetDropTwitchVods";
import { useGetDropKickVods } from "@/hooks/drop/useGetDropKickVods";
import { useGetDropYouTubeVideos } from "@/hooks/drop/useGetDropYouTubeVideos";
import { formatDuration, formatViews, timeAgo, platformIcon, isPlaceholderTwitchThumb, type StreamingSource, type SelectedClip } from "./shared";

interface HighlightItem {
    key: string;
    id: string;
    source: StreamingSource;
    title: string;
    url: string;
    thumbnailUrl: string;
    when: string;
    durationSeconds: number;
    viewCount: number;
    hlsUrl?: string;
}

interface Props {
    /** Drop slug — drives every content query in this component. */
    dropSlug?: string;
    hubName?: string;
    selectedClipId?: string;
    onSelectClip?: (clip: SelectedClip | null) => void;
}

/*
 * `when` is required by the HighlightItem shape but is never rendered for
 * placeholders (the !isPlaceholder guard in the meta row suppresses
 * `timeAgo(c.when)`). Any valid date string works — `new Date().toISOString()`
 * keeps the type honest without introducing a magic sentinel.
 */
const PLACEHOLDER_HIGHLIGHTS: HighlightItem[] = [
    { key: "ph-1", id: "ph-1", source: "TWITCH", title: "Best moments will surface here", url: "#", thumbnailUrl: "", when: new Date().toISOString(), durationSeconds: 0, viewCount: 0 },
    { key: "ph-2", id: "ph-2", source: "KICK", title: "Top clips by view count", url: "#", thumbnailUrl: "", when: new Date().toISOString(), durationSeconds: 0, viewCount: 0 },
    { key: "ph-3", id: "ph-3", source: "YOUTUBE", title: "Featured stream highlights", url: "#", thumbnailUrl: "", when: new Date().toISOString(), durationSeconds: 0, viewCount: 0 },
    { key: "ph-4", id: "ph-4", source: "TWITCH", title: "Curated by the creator", url: "#", thumbnailUrl: "", when: new Date().toISOString(), durationSeconds: 0, viewCount: 0 },
];

export const HighlightsRow: React.FC<Props> = ({ dropSlug, hubName, selectedClipId, onSelectClip }) => {
    /*
     * All content comes from the Drop endpoints (/drops/slug/:slug/content/*).
     * This used to be a two-branch fetch with a Hub fallback and a `surfaceId`
     * prop to drive it; the Hub endpoints are gone, so the prop and the branch
     * went with them. Each hook self-disables via `enabled: Boolean(dropSlug)`.
     */
    const dropTwitchClips = useGetDropTwitchClips(dropSlug);
    const dropTwitchVods = useGetDropTwitchVods(dropSlug);
    const dropKickVods = useGetDropKickVods(dropSlug);
    const dropYoutubeVids = useGetDropYouTubeVideos(dropSlug);
    const twitchClips = dropTwitchClips;
    const twitchVods = dropTwitchVods;
    const kickVods = dropKickVods;
    const youtubeVids = dropYoutubeVids;

    /*
     * Aggregate + sort the 4 query results into a single timeline. React Query
     * caches the underlying data refs across renders, so the memo only re-runs
     * when one of the four `data` references actually changes — avoids
     * re-sorting + re-allocating on every parent-driven re-render (clip
     * selection, tab switch, etc.).
     */
    const items: HighlightItem[] = useMemo(() => {
        const all: HighlightItem[] = [
            ...((twitchClips.data as { clips?: Array<{ id: string; title: string; url: string; thumbnailUrl: string; createdAt: string; durationSeconds: number; viewCount: number }> } | null | undefined)?.clips ?? [])
                .map(c => ({ key: `tc-${c.id}`, id: c.id, source: "TWITCH" as const, title: c.title, url: c.url, thumbnailUrl: c.thumbnailUrl, when: c.createdAt, durationSeconds: c.durationSeconds, viewCount: c.viewCount })),
            ...((twitchVods.data as { vods?: Array<{ id: string; title: string; url: string; thumbnailUrl: string; createdAt: string; durationSeconds: number; viewCount: number }> } | null | undefined)?.vods ?? [])
                .map(v => ({ key: `tv-${v.id}`, id: v.id, source: "TWITCH" as const, title: v.title, url: v.url, thumbnailUrl: v.thumbnailUrl, when: v.createdAt, durationSeconds: v.durationSeconds, viewCount: v.viewCount })),
            ...((kickVods.data as { vods?: Array<{ id: string; title: string; url: string; thumbnailUrl: string; createdAt: string; durationSeconds: number; viewCount: number; hlsUrl?: string }> } | null | undefined)?.vods ?? [])
                .map(v => ({ key: `kv-${v.id}`, id: v.id, source: "KICK" as const, title: v.title, url: v.url, thumbnailUrl: v.thumbnailUrl, when: v.createdAt, durationSeconds: v.durationSeconds, viewCount: v.viewCount, hlsUrl: v.hlsUrl })),
            ...((youtubeVids.data as { videos?: Array<{ videoId: string; title: string; thumbnailUrl: string; publishedAt: string; durationSeconds?: number; viewCount?: number }> } | null | undefined)?.videos ?? [])
                .map(v => ({ key: `yt-${v.videoId}`, id: v.videoId, source: "YOUTUBE" as const, title: v.title, url: `https://www.youtube.com/watch?v=${v.videoId}`, thumbnailUrl: v.thumbnailUrl, when: v.publishedAt, durationSeconds: v.durationSeconds ?? 0, viewCount: v.viewCount ?? 0 })),
        ];
        /*
         * Drop entries with unparseable `when` — `new Date("not-a-date").getTime()`
         * is NaN, which makes the sort comparator return NaN for every pair
         * involving the bad row → non-deterministic ordering → a malformed
         * upstream record could nondeterministically land at index 0 and feed
         * the hero. Skip them entirely; a real VOD always has a valid date.
         */
        const valid = all.filter(item => !Number.isNaN(new Date(item.when).getTime()));
        valid.sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
        return valid.length > 0 ? valid.slice(0, 4) : PLACEHOLDER_HIGHLIGHTS;
    }, [twitchClips.data, twitchVods.data, kickVods.data, youtubeVids.data]);
    const isPlaceholder = items === PLACEHOLDER_HIGHLIGHTS;

    return (
        <section className="hcs-highlights" aria-label="Highlights">
            <header className="hcs-highlights__header">
                <h3 className="hcs-highlights__title">Highlights</h3>
                <span className="hcs-highlights__sub">{isPlaceholder ? "Will appear when content is available" : `Latest from ${hubName ?? "this creator"}`}</span>
            </header>
            <div className="hcs-highlights__row">
                {items.map(c => {
                    /*
                     * Promotion carve-outs (mirrors ClipsTab):
                     *   - Kick VODs require an HLS source URL to play inline
                     *     (no public VOD iframe). When Kick's API returned VOD
                     *     metadata without the m3u8 source, link out instead —
                     *     the alternative is a blank inline player.
                     *   - Placeholders are inert (used when the creator has no
                     *     content yet); the card structure IS the empty state.
                     *
                     * Everything else (Twitch clips + VODs, YouTube clips +
                     * VODs) is promotable into the StreamPanel inline player.
                     */
                    const kickMissingHls = c.source === "KICK" && !c.hlsUrl;
                    const promotable = !isPlaceholder && !kickMissingHls && Boolean(onSelectClip);
                    const isSelected = !isPlaceholder && selectedClipId === c.id;
                    /*
                     * For the link-out fallback path, guard against non-http(s)
                     * schemes (data:, blob:, javascript:). React strips
                     * `javascript:` from <a href> automatically but `data:` is
                     * not stripped and would open an attacker-controlled page
                     * in a new tab. Defends against an upstream regression
                     * where a creator-supplied URL flows through unsanitized.
                     */
                    const isSafeFallbackUrl = !isPlaceholder &&
                        (c.url.startsWith("https://") || c.url.startsWith("http://"));
                    const handleClick = (e: React.MouseEvent) => {
                        if (!promotable) return;
                        e.preventDefault();
                        onSelectClip!({
                            id: c.id,
                            source: c.source,
                            url: c.url,
                            title: c.title,
                            thumbnailUrl: c.thumbnailUrl,
                            hlsUrl: c.hlsUrl,
                        });
                    };
                    return (
                        /*
                         * Anchor is intentional — when the click promotes the
                         * clip into the player we preventDefault, but for
                         * Kick-without-HLS the href + target=_blank is the
                         * genuine fallback navigation. Mirrors ClipsTab.
                         */
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                        <a
                            key={c.key}
                            className={`hcs-highlights__card${isPlaceholder ? " is-placeholder" : ""}${isSelected ? " is-selected" : ""}`}
                            href={isSafeFallbackUrl ? c.url : undefined}
                            target={promotable || !isSafeFallbackUrl ? undefined : "_blank"}
                            rel={promotable || !isSafeFallbackUrl ? undefined : "noopener noreferrer"}
                            onClick={handleClick}
                        >
                            <div className="hcs-highlights__thumb">
                                {/*
                                  * referrerPolicy="no-referrer" — Twitch's
                                  * CDN occasionally 403s hotlinked
                                  * thumbnails when the Referer header
                                  * carries our origin. Suppressing the
                                  * header makes the request look like
                                  * an unattributed image fetch and the
                                  * CDN serves it. Pattern matches the
                                  * fix already in place on
                                  * DropInCreatorCard.
                                  *
                                  * onError fallback swaps to the play-only
                                  * placeholder so a broken image never
                                  * paints — better than the chrome
                                  * broken-image icon.
                                  */}
                                {c.thumbnailUrl && !isPlaceholderTwitchThumb(c.thumbnailUrl)
                                    ? <img
                                        src={c.thumbnailUrl}
                                        alt={c.title}
                                        loading="lazy"
                                        referrerPolicy="no-referrer"
                                        onError={(e) => {
                                            const img = e.currentTarget;
                                            img.style.display = "none";
                                            const parent = img.parentElement;
                                            if (parent && !parent.querySelector(".hcs-highlights__thumb-empty")) {
                                                const fallback = document.createElement("div");
                                                fallback.className = "hcs-highlights__thumb-empty";
                                                fallback.innerHTML = "<svg viewBox=\"0 0 24 24\" width=\"24\" height=\"24\" fill=\"currentColor\"><path d=\"M8 5v14l11-7z\"/></svg>";
                                                parent.appendChild(fallback);
                                            }
                                        }}
                                    />
                                    : <div className="hcs-highlights__thumb-empty"><FaPlay /></div>}
                                <span className={`hcs-highlights__badge hcs-highlights__badge--${c.source.toLowerCase()}`}>
                                    {platformIcon(c.source)}
                                </span>
                                {c.durationSeconds > 0 && <span className="hcs-highlights__duration">{formatDuration(c.durationSeconds)}</span>}
                                {isSelected && <span className="hcs-highlights__playing">PLAYING</span>}
                            </div>
                            <div className="hcs-highlights__title-line">{c.title}</div>
                            <div className="hcs-highlights__meta">
                                {c.viewCount > 0 ? <><FaEye /> {formatViews(c.viewCount)}</> : null}
                                {!isPlaceholder && <><FaClock /> {timeAgo(c.when)}</>}
                            </div>
                        </a>
                    );
                })}
            </div>
        </section>
    );
};
